import type { CheckoutSummary } from '../types/api';
import { getStepLabel } from './labels';

export type NamedCount = {
  key: string;
  label: string;
  value: number;
};

export type TimeBucketRow = {
  label: string;
  abandoned: number;
  converted: number;
};

const TIME_BUCKETS: { label: string; maxMs: number | null }[] = [
  { label: '< 5 min', maxMs: 5 * 60 * 1000 },
  { label: '5–15 min', maxMs: 15 * 60 * 1000 },
  { label: '15–30 min', maxMs: 30 * 60 * 1000 },
  { label: '30–60 min', maxMs: 60 * 60 * 1000 },
  { label: '1–2 h', maxMs: 2 * 60 * 60 * 1000 },
  { label: '2–6 h', maxMs: 6 * 60 * 60 * 1000 },
  { label: '6–24 h', maxMs: 24 * 60 * 60 * 1000 },
  { label: '> 24 h', maxMs: null }
];

function durationMs(row: CheckoutSummary): number | null {
  const start = Date.parse(row.firstEventAt);
  const end = Date.parse(row.lastEventAt);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return null;
  return end - start;
}

function bucketIndex(ms: number): number {
  for (let i = 0; i < TIME_BUCKETS.length; i += 1) {
    const max = TIME_BUCKETS[i]?.maxMs ?? null;
    if (max === null || ms < max) return i;
  }
  return TIME_BUCKETS.length - 1;
}

/** Abandonos agrupados pela última etapa conhecida. */
export function abandonmentsByStep(rows: readonly CheckoutSummary[]): NamedCount[] {
  const counts = new Map<string, number>();
  for (const row of rows) {
    if (row.status !== 'abandoned') continue;
    const key = row.step?.trim() || '__unknown__';
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  return [...counts.entries()]
    .map(([key, value]) => ({
      key,
      label: key === '__unknown__' ? 'Não informado' : getStepLabel(key),
      value
    }))
    .sort((a, b) => b.value - a.value);
}

/** Intervalo entre primeira e ultima atividade, agrupado pelo status atual. */
export function activityDurationHistogram(rows: readonly CheckoutSummary[]): TimeBucketRow[] {
  const buckets = TIME_BUCKETS.map((bucket) => ({
    label: bucket.label,
    abandoned: 0,
    converted: 0
  }));

  for (const row of rows) {
    if (row.status !== 'abandoned' && row.status !== 'converted') continue;
    const ms = durationMs(row);
    if (ms === null) continue;
    const index = bucketIndex(ms);
    const bucket = buckets[index];
    if (!bucket) continue;
    if (row.status === 'abandoned') bucket.abandoned += 1;
    else bucket.converted += 1;
  }

  return buckets;
}
